// risk-calc.js — pure risk computations (single source of truth for the Risk page)
// No DOM. All values currency-correct via hVal/toUSD/hTotUSD. Mirrors the audited formulas
// previously inlined in dashboard._dashRisk, trades._trNeo, portfolio concentration/rebalance,
// and dashboard.generateMarketIntel. Those inline copies are removed at S6 (de-dup), leaving
// these functions as the only implementation.

import { S } from './state.js';
import { hVal, hTotUSD, hWealthUSD, toUSD, normTicker } from './math.js';

// Gross holdings value in USD (sum of priced holding values) — matches dashboard gVal.
export function grossHoldingsUSD() {
  let g = 0;
  S.holdings.forEach(h => { const v = hVal(h); if (v.vU) g += v.vU; });
  return g;
}

// Canonical net-worth provider, injected by main.js (dashboard.computeTotalWealthUSD) to avoid an
// import cycle. Pure + order-independent, so the Risk page is correct regardless of render order.
let _nwProvider = null;
export function registerNetWorth(fn) { _nwProvider = fn; }
/**
 * Has the canonical provider actually been wired?
 *
 * `netWorthUSD()` falls back twice when it has not — to `window.__twUSD`, which is written only
 * when the Dashboard RENDERS, and then to `grossHoldingsUSD() + cash`, which is a net worth with
 * no reserve fund, no accrued time-deposit interest and no unpriced holdings. That last one is
 * the formula CONTRACT-003 deleted from `goals.js`, still standing here as a fallback. Neither
 * announces itself, so `netWorth.singleSource` needs to be able to ask. (2026-08-16)
 */
export function _nwProviderWired() { return typeof _nwProvider === 'function'; }

// Same pattern for the TRY balance. `S.cash.TRY` is the last bank reading plus ledger moves;
// household bills confirmed since then are not in it, so it overstates what the account holds.
// cashflow.cashTRYNow() knows the difference and this file may not import a module to ask.
let _tryProvider = null;
export function registerCashTRY(fn) { _tryProvider = fn; }
function _cashTRY() {
  if (_tryProvider) { const v = _tryProvider(); if (typeof v === 'number' && isFinite(v)) return v; }
  return (S.cash && S.cash.TRY) || 0;
}

// Net worth USD — provider first (fresh, full formula), then the shared global, then gross+cash.
export function netWorthUSD() {
  if (_nwProvider) { const v = _nwProvider(); if (v) return v; }
  const nw = (typeof window !== 'undefined' && window.__twUSD) || 0;
  if (nw) return nw;
  const cash = (S.cash?.USD || 0) + toUSD(_cashTRY(), 'TRY') + toUSD(S.cash?.EUR || 0, 'EUR');
  return grossHoldingsUSD() + cash;
}

// ===== 1. CONCENTRATION =====
// HHI (sum of squared % weights), top-1 / top-3 / top-5, per-type concentration, per-position contribution.
export function riskConcentration() {
  const gVal = grossHoldingsUSD();
  const holdVals = S.holdings
    .map(h => { const v = hVal(h); return { ticker: h.ticker, type: h.type, val: v.vU || 0 }; })
    .filter(h => h.val > 0)
    .sort((a, b) => b.val - a.val);

  if (!gVal || !holdVals.length) {
    return { gVal: 0, holdVals: [], top1: null, top1Pct: 0, top3Pct: 0, top5Pct: 0, hhi: 0, rating: '—', ratingColor: 'var(--t3)', typeConc: [], positions: [] };
  }

  const top1 = holdVals[0];
  const top1Pct = (top1.val / gVal) * 100;
  const top3Pct = holdVals.slice(0, 3).reduce((s, h) => s + h.val, 0) / gVal * 100;
  const top5Pct = holdVals.slice(0, 5).reduce((s, h) => s + h.val, 0) / gVal * 100;
  const hhi = holdVals.reduce((s, h) => { const w = h.val / gVal * 100; return s + w * w; }, 0);
  const rating = hhi > 2500 ? 'High' : hhi > 1500 ? 'Moderate' : 'Diversified';
  const ratingColor = hhi > 2500 ? 'var(--red)' : hhi > 1500 ? 'var(--yel)' : 'var(--grn)';

  const typeVals = {};
  holdVals.forEach(h => { typeVals[h.type] = (typeVals[h.type] || 0) + h.val; });
  const typeConc = Object.entries(typeVals)
    .map(([type, v]) => ({ type, pct: v / gVal * 100 }))
    .sort((a, b) => b.pct - a.pct);

  const positions = holdVals.map(h => ({ ticker: h.ticker, type: h.type, val: h.val, pct: h.val / gVal * 100 }));

  return { gVal, holdVals, top1, top1Pct, top3Pct, top5Pct, hhi, rating, ratingColor, typeConc, positions };
}

// ===== 2. DRAWDOWN & VOLATILITY =====
/**
 * Drawdown and volatility from the wealth snapshot series.
 *
 * TWO DEFECTS FIXED HERE (audit 2026-08-01), both of which made the numbers meaningless
 * rather than merely imprecise:
 *
 * 1. CONTRIBUTION CONTAMINATION. Returns were `(W1 - W0) / W0` on totalWealth, which
 *    includes cash and every deposit. A salary or bonus therefore appeared as a large
 *    positive return: it inflated volatility and, because deposits only push wealth up,
 *    it systematically MASKED real drawdowns. Where both endpoints carry netExtCapital
 *    the external flow is now removed, so the return is an investment return. Snapshots
 *    written before that field existed fall back to the wealth basis, and `basis` says
 *    which was used so the UI cannot imply more than the data supports.
 *
 * 2. BLIND √252. Snapshots are written once per day the app is OPENED, so a five-day gap
 *    was treated as one daily return and then annualised as if it were one trading day —
 *    overstating annualised vol by roughly √5 for a weekly user. Annualisation now uses
 *    the observed median spacing: periodsPerYear = 365 / medianGapDays.
 *
 * The series is also sorted defensively: live state was found unsorted, and every
 * peak-to-trough walk below depends on order.
 */
export function riskDrawdownVol() {
  const snaps = (S.snapshots || [])
    .filter(s => s && s.totalWealth)
    .slice()
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));

  let maxDD = 0, peakVal = 0;
  snaps.forEach(snap => {
    if (snap.totalWealth > peakVal) peakVal = snap.totalWealth;
    const dd = peakVal > 0 ? (snap.totalWealth - peakVal) / peakVal * 100 : 0;
    if (dd < maxDD) maxDD = dd;
  });
  const nw = netWorthUSD();
  const curDD = peakVal > 0 ? (nw - peakVal) / peakVal * 100 : 0;

  let dailyVol = null, annualVol = null, basis = 'wealth', adjustedPairs = 0, medianGapDays = null;
  if (snaps.length >= 5) {
    const returns = [];
    const gaps = [];
    for (let i = 1; i < snaps.length; i++) {
      const a = snaps[i - 1], b = snaps[i];
      if (!(a.totalWealth > 0) || !(b.totalWealth > 0)) continue;
      // Remove the external flow between the two observations when both recorded it.
      //
      // ⚠ CONTRACT-002 — A RECORDED ZERO IS THE UNRECONCILED SENTINEL, NOT A MEASUREMENT.
      //
      // `_dashSnapshot` wrote `netExtCapital: D.netExtCapital`, and `D.netExtCapital` is
      // `_ngOk ? _ng.netExtCapital : 0` — so a day the capital identity did not close was
      // persisted as **zero external capital**, which is a different claim entirely.
      // `netGrowthUSD` itself returns `null` for that case; only the dashboard's display
      // fallback ever produces the 0, and it reached the snapshot.
      //
      // `typeof === 'number'` accepts it, and the pair either side then reads as a flow the
      // size of the WHOLE contributed capital. Measured on his 64 snapshots 2026-08-15:
      // fifteen carry the field and **two are zero — 2026-08-02 and 2026-08-06** — producing
      //
      //   02 → 03   flow  +$70,195        05 → 06   flow  −$70,285
      //   06 → 07   flow  +$70,342
      //
      // three phantom flows of ~$70k in a series whose real daily moves are hundreds of
      // dollars. Volatility and drawdown are computed from exactly these returns.
      //
      // Guarding on `> 0` rather than `typeof number` fixes the READER, which is what reaches
      // the two rows already on disk — a `null` writer alone would be a fix for the next book
      // (rule #28). Zero is never a legitimate value here: `netGrowthUSD` requires
      // `netExtCapital > 0` before it reports at all.
      let flow = 0;
      if (a.netExtCapital > 0 && b.netExtCapital > 0) {
        flow = b.netExtCapital - a.netExtCapital;
        adjustedPairs++;
      }
      returns.push((b.totalWealth - a.totalWealth - flow) / a.totalWealth);
      const g = (Date.parse(b.date) - Date.parse(a.date)) / 86400000;
      if (isFinite(g) && g > 0) gaps.push(g);
    }
    if (returns.length >= 3) {
      const mean = returns.reduce((s, r) => s + r, 0) / returns.length;
      const variance = returns.reduce((s, r) => s + Math.pow(r - mean, 2), 0) / returns.length;
      dailyVol = Math.sqrt(variance) * 100;
      if (gaps.length) {
        const sorted = gaps.slice().sort((x, y) => x - y);
        medianGapDays = sorted[Math.floor(sorted.length / 2)];
      }
      const periodsPerYear = medianGapDays > 0 ? 365 / medianGapDays : 365;
      annualVol = dailyVol * Math.sqrt(periodsPerYear);
      basis = adjustedPairs === returns.length ? 'investment' : adjustedPairs > 0 ? 'mixed' : 'wealth';
    }
  }
  return { maxDD, curDD, peakVal, nw, dailyVol, annualVol, snapCount: snaps.length, basis, adjustedPairs, medianGapDays };
}

// ===== THE DEFAULT STOP — ONE DEFINITION =====
//
// ⚠ HORIZ-002. The "no SL set → 25% adverse from entry" display rule was restated as a bare
// `t.entry * 0.75 : t.entry * 1.25` at FIVE sites: trades.js, dashboard.js twice, here, and
// ai-recommend.js's R2.
//
// A sixth site had already been FIXED and the reasoning written down — `ai-recommend.js`'s R1,
// audit W2-2:
//
//   > at 3-5x a flat 25% stop sits AT/BELOW the liquidation point — a stop that can never
//   > execute. The suggested adverse distance is min(25%, 70%/lev).
//
// That correction reached one call site and stayed there, **in a file whose other call site
// still used the flat number**. So the app recommended a leverage-aware stop and then measured
// its own open risk against a different, impossible one. Rule #25: the unit of a sweep is the
// SHAPE, across src/.
//
// The arithmetic it exists for: liquidation is 1/lev adverse. At 4x the flat 25% IS the
// liquidation price; at 5x it is beyond it, and `riskUSD = |entry - sl| x qty x lev` then claims
// 1.25x the margin can be lost on a position that closes itself at 1.0x.
//
// ⚠ `signals.js:1252` also compares against `entry * 0.75` and is NOT an instance of this rule:
// it grades a SIGNAL's forward outcome, and a signal has no leverage, no qty and no margin.
// A coincidental number, deliberately left alone.
export const DEFAULT_SL_ADVERSE = 0.25;

/** Adverse fraction for a trade with no stop: never at or beyond liquidation. */
export function defaultStopAdverse(leverage) {
  const lev = (typeof leverage === 'number' && leverage > 0) ? leverage : 1;
  return Math.min(DEFAULT_SL_ADVERSE, 0.7 / lev);
}

/** The stop this trade is measured against: its own if set, else the default rule. */
export function effectiveStop(t) {
  if (!t) return null;
  if (t.sl != null) return t.sl;
  const entry = Number(t.entry);
  if (!isFinite(entry)) return null;
  const adverse = defaultStopAdverse(t.leverage || 1);
  return t.side === 'Long' ? entry * (1 - adverse) : entry * (1 + adverse);
}

// ===== 3. LEVERAGE & OPEN-TRADE RISK =====
// Capital-at-risk (entry→SL × qty × lev), reward @TP1, R:R, % of net worth, leverage exposure,
// SL-less open trades (default SL = 25% adverse from entry — display rule), per-trade R-multiples.
export function riskOpenTrades() {
  const open = S.trades.filter(t => t.status !== 'Closed');
  const priced = open.filter(t => t.type !== 'TimeDeposit');

  const rm = [];
  let slLessCount = 0;
  priced.forEach(t => {
    const cur = t.currency || 'USD';
    const lev = t.leverage || 1;
    const p = S.prices[t.ticker];
    const price = p ? p.price : null;
    const isDefSL = t.sl == null;
    if (isDefSL) slLessCount++;
    const slEff = effectiveStop(t);
    // A TP1 is required to compute a REWARD, never to compute RISK. The old early return
    // sat before riskUSD, so every stop-defined trade with no take-profit target — the
    // normal shape for a long-term position with downside protection — was dropped from
    // "Open Risk" and "% of net worth" entirely, and from the leverage component of
    // riskScore with it. The panel could show "N SL-less" beside $0 at risk.
    // (audit 2026-08-01)
    if (price == null) return;
    const hasTp = t.tp1 != null;
    const riskUnit = Math.abs(t.entry - slEff);
    const Rtp = (hasTp && riskUnit) ? (Math.abs(t.tp1 - t.entry) / riskUnit) : null;
    const curR = riskUnit ? ((t.side === 'Long' ? price - t.entry : t.entry - price) / riskUnit) : null;
    const riskUSD = toUSD(riskUnit * t.qty * lev, cur);
    const rewardUSD = hasTp ? toUSD(Math.abs(t.tp1 - t.entry) * t.qty * lev, cur) : 0;
    rm.push({ ticker: t.ticker, side: t.side, lev, isDefSL, hasTp, Rtp, curR, riskUSD, rewardUSD });
  });

  const withTP = rm.filter(x => x.Rtp != null);
  // Risk over EVERY priced open trade; reward and R:R only where a target exists.
  const totRisk = rm.reduce((s, r) => s + r.riskUSD, 0);
  const totReward = withTP.reduce((s, r) => s + r.rewardUSD, 0);
  const rr = totRisk ? (totReward / totRisk) : 0;
  const nw = netWorthUSD();
  const pctNW = nw ? (totRisk / nw * 100) : null;

  const levTrades = open.filter(t => t.leverage > 1);
  const levExposure = levTrades.reduce((s, t) => s + toUSD(t.entry * t.qty * (t.leverage || 1), t.currency || 'USD'), 0);
  const slLessLeveraged = open.filter(t => t.leverage > 1 && t.sl == null);

  return {
    openCount: open.length,
    rMultiples: withTP.sort((a, b) => b.Rtp - a.Rtp),
    totRisk, totReward, rr, pctNW,
    levTrades, levExposure,
    slLessCount, slLessLeveraged
  };
}

// ===== 4. FX EXPOSURE =====
// Per-currency USD-equivalent across holdings + cash + open-trade capital. Matches trades #trFx.
export function riskFxExposure() {
  const b = { USD: 0, TRY: 0, EUR: 0 };
  (S.holdings || []).forEach(h => { const cur = h.currency || 'USD'; const vu = hWealthUSD(h).usd; if (!vu) return; b[cur] = (b[cur] || 0) + vu; });
  // TRY on the NOW basis, so the bucket and the net worth it is divided by agree.
  ['USD', 'TRY', 'EUR'].forEach(c => { b[c] += toUSD(c === 'TRY' ? _cashTRY() : ((S.cash && S.cash[c]) || 0), c); });
  (S.trades || []).filter(t => t.status !== 'Closed').forEach(t => {
    const cur = t.currency || 'USD';
    const cap = (t.type === 'TimeDeposit' && t.td) ? t.td.principal : (t.entry * t.qty);
    b[cur] = (b[cur] || 0) + toUSD(cap, cur);
  });
  const total = Object.values(b).reduce((s, v) => s + v, 0) || 1;
  const breakdown = Object.entries(b)
    .filter(([, v]) => v > 0)
    .sort((x, y) => y[1] - x[1])
    .map(([ccy, v]) => ({ ccy, usd: v, pct: v / total * 100 }));
  return { buckets: b, total, breakdown };
}

// ===== 5. ALLOCATION DRIFT =====
// Type-level target (sum of allocationPct) vs actual value-weight. Mirrors portfolio rebalance check.
// Scope: the default/Retirement portfolio only — the single sleeve that carries allocationPct
// targets. Including SAP/crypto/trade portfolio VALUES in the actual-weight denominator while
// their targets are 0 fabricated large fake drift (fixed 2026-07-24; targets sum to 100 since Option A).
export function driftHoldings() {
  const ps = S.portfolios || [];
  const ret = ps.find(p => p.isDefault) || ps.find(p => /retire/i.test(p.name || ''));
  return ret ? S.holdings.filter(h => h.portfolioId === ret.id) : S.holdings;
}

/**
 * Does this set of holdings run a target regime at all? SAP XETRA and Crypto-Medium Term
 * carry allocationPct:0 on every row — not cut, just never targeted. A drift read against
 * targets that sum to zero is 100% off by construction and says nothing (same scoping as
 * positions.cutNotExited, c49832b). The day a target is set on such a sleeve it starts
 * being read, with no further change here.
 */
export function sleeveHasTargets(holdings) {
  return (holdings || []).reduce((a, h) => a + ((h && h.allocationPct) || 0), 0) > 0;
}

export function riskAllocationDrift() {
  const scoped = driftHoldings();
  const ta = {};
  scoped.forEach(h => { ta[h.type] = (ta[h.type] || 0) + (h.allocationPct || 0); });
  const td = {};
  scoped.forEach(h => { if (!td[h.type]) td[h.type] = { val: 0 }; const v = hVal(h); if (v.vU) td[h.type].val += v.vU; });
  const totalVal = Object.values(td).reduce((s, d) => s + d.val, 0);
  const targetSum = Object.values(ta).reduce((s, v) => s + v, 0);
  const threshold = parseFloat(S.settings?.rebalThreshold) || 5;

  const rows = Object.entries(ta).map(([type, targetPct]) => {
    const actualVal = td[type] ? td[type].val : 0;
    const actualPct = totalVal > 0 ? (actualVal / totalVal) * 100 : 0;
    const dev = actualPct - targetPct;
    return { type, targetPct, actualPct, dev, off: Math.abs(dev) > threshold };
  }).sort((a, b) => Math.abs(b.dev) - Math.abs(a.dev));

  const offCount = rows.filter(r => r.off).length;
  // Targets considered incomplete when they don't sum near 100% — drift unreliable.
  const targetsComplete = targetSum >= 95 && targetSum <= 105;

  return { rows, threshold, offCount, totalVal, targetSum, targetsComplete };
}

// ===== COMPOSITE RISK SCORE (0-100 health; higher = healthier) =====
// Transparent weighting over 4 domains. Drift excluded (data-quality caveat). Unavailable
// components (e.g. no snapshots) drop out and remaining weights renormalize.
const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));
const lin = (v, good, bad) => clamp((bad - v) / (bad - good) * 100); // v=good→100, v=bad→0

export function riskScore(parts) {
  const C = (parts && parts.C) || riskConcentration();
  const D = (parts && parts.D) || riskDrawdownVol();
  const T = (parts && parts.T) || riskOpenTrades();
  const X = (parts && parts.X) || riskFxExposure();

  const comps = [];

  // Concentration: HHI 1000 (healthy) → 3000 (bad)
  comps.push({
    key: 'concentration', label: 'Concentration', weight: 0.30,
    available: C.positions.length > 0,
    score: C.positions.length ? lin(C.hhi, 1000, 3000) : null,
    note: C.positions.length ? `HHI ${Math.round(C.hhi)} · ${C.rating.toLowerCase()}` : 'no holdings'
  });

  // Drawdown: current DD 0% (healthy) → -20% (bad). Needs snapshots.
  comps.push({
    key: 'drawdown', label: 'Drawdown', weight: 0.25,
    available: D.snapCount >= 2,
    score: D.snapCount >= 2 ? lin(Math.abs(Math.min(D.curDD, 0)), 0, 20) : null,
    note: D.snapCount >= 2 ? `current DD ${D.curDD < 0 ? D.curDD.toFixed(1) + '%' : 'at peak'}` : 'no snapshots'
  });

  // Leverage/open risk: capital-at-risk 0% (healthy) → 10% of net worth (bad);
  // SL-less leveraged trades subtract a penalty CAPPED at 40 so count alone can't zero the
  // component — the at-risk magnitude still drives the rest. audit M-4.
  let levScore = 100;
  if (T.pctNW != null) levScore = lin(T.pctNW, 0, 10);
  const slLessPenalty = Math.min(T.slLessLeveraged.length * 15, 40);
  levScore = clamp(levScore - slLessPenalty);
  comps.push({
    key: 'leverage', label: 'Leverage & Trade Risk', weight: 0.25,
    available: true,
    score: levScore,
    note: T.pctNW != null
      ? `${T.pctNW.toFixed(1)}% of NW at risk${T.slLessLeveraged.length ? ` · ${T.slLessLeveraged.length} SL-less lev` : ''}`
      : (T.slLessLeveraged.length ? `${T.slLessLeveraged.length} SL-less leveraged trade(s)` : 'no defined trade risk')
  });

  // FX: top currency weight 40% (healthy) → 90% (bad)
  const topFx = X.breakdown[0];
  comps.push({
    key: 'fx', label: 'FX Concentration', weight: 0.20,
    available: !!topFx,
    score: topFx ? lin(topFx.pct, 40, 90) : null,
    note: topFx ? `${topFx.ccy} ${topFx.pct.toFixed(0)}% of exposure` : 'no exposure'
  });

  const active = comps.filter(c => c.available && c.score != null);
  const wSum = active.reduce((s, c) => s + c.weight, 0) || 1;
  const overall = Math.round(active.reduce((s, c) => s + c.score * c.weight, 0) / wSum);
  const band = overall >= 70 ? 'Healthy' : overall >= 40 ? 'Elevated' : 'High';
  const color = overall >= 70 ? 'var(--grn)' : overall >= 40 ? 'var(--yel)' : 'var(--red)';
  const worst = active.slice().sort((a, b) => a.score - b.score)[0] || null;

  return { overall, band, color, components: comps, worst };
}

// ===== STRESS-TEST (interactive, existing data only) =====
// assetDropPct: adverse move on risk assets (non-Cash holdings + open-trade underlyings, leverage-aware).
// tryDepreciationPct: TRY weakening vs USD, hits USD value of TRY-denominated assets + TRY cash.
export function stressTest(assetDropPct = 0, tryDepreciationPct = 0) {
  const d = Math.max(0, assetDropPct) / 100;
  const z = Math.max(0, tryDepreciationPct) / 100;

  let assetLoss = 0;
  (S.holdings || []).forEach(h => {
    if (h.type === 'Cash') return;
    const vu = hVal(h).vU; if (!vu) return;
    assetLoss += vu * d;
  });

  let leverageLoss = 0;
  (S.trades || []).filter(t => t.status !== 'Closed' && t.type !== 'TimeDeposit').forEach(t => {
    const notional = toUSD((t.entry || 0) * (t.qty || 0), t.currency || 'USD');
    leverageLoss += notional * (t.leverage || 1) * d;
  });

  let tryLoss = 0;
  (S.holdings || []).forEach(h => {
    if ((h.currency || 'USD') !== 'TRY') return;
    const vu = hVal(h).vU; if (!vu) return;
    tryLoss += vu * z;
  });
  tryLoss += toUSD(_cashTRY(), 'TRY') * z;

  const base = netWorthUSD();
  const totalImpact = -(assetLoss + leverageLoss + tryLoss);
  const newWealth = base + totalImpact;
  const impactPct = base ? (totalImpact / base * 100) : 0;

  return { assetLoss, leverageLoss, tryLoss, totalImpact, base, newWealth, impactPct };
}

// ===== 8. EXPOSURE — HOW BIG IS THE POSITION IN THE FIRST PLACE =====
/**
 * `tradeExposure()` — every open trade at CURRENT market value, as a share of net worth, with the
 * same name's portfolio holding beside it.
 *
 * ⚠ **THIS IS THE SIBLING OF `riskOpenTrades()`, NOT A SECOND COPY OF IT.** Open Risk answers
 * *"what leaves if every stop hits"* — `|entry − stop| × qty × lev`. This answers *"how big is
 * this position at all"* — `price × qty × lev`. A trade with a tight stop is small in the first
 * and can be his largest single name in the second, which is exactly why one panel cannot answer
 * for the other.
 *
 * ⚠ **NOTIONAL AT CURRENT PRICE, NOT CAPITAL AT COST.** `entry × qty` is what he paid and it is
 * already on the page as invested capital; exposure is what a move does to him *today*, so it
 * reads the live price. The leverage multiplier belongs on this side: a 3× position moves three
 * times as hard whatever the margin was. His book is all 1× today (measured, 2026-08-16), so the
 * multiplier changes nothing yet and is written in because the day it does, silence would be a
 * wrong number rather than a missing feature.
 *
 * ⚠ **THE DENOMINATOR IS `netWorthUSD()` AND NOTHING ELSE** — trap #13, the one accessor. Drafting
 * this measurement by hand produced $149.433, then $44.609, then $53.613, then $77.995 for the
 * same book on the same day; the function says $79.108. A percentage of a net worth nobody else
 * uses is not a percentage of his net worth. ⚠ In a cold test harness the provider is unwired and
 * `netWorthUSD()` returns its gross+cash FALLBACK — a third smaller. A test asserting a percentage
 * must register the provider or say in writing that it is asserting against the fallback.
 *
 * ⚠ **AN UNPRICED TRADE IS COUNTED AS MISSING, NOT AS ZERO.** It is dropped from the total and
 * reported in `unpriced`, because a position the page silently leaves out reads as a smaller book
 * rather than an incomplete one. Same for a holding overlap that has no price: `heldUnpriced`.
 *
 * ⚠ **TIME DEPOSITS ARE NOT MARKET EXPOSURE.** A deposit does not move with a price; counting its
 * principal would inflate the headline by its full value and mean nothing. Excluded on `type`
 * alone — never on `t.td`, which can be missing on a malformed row (rule #51).
 *
 * Returns `{ rows, totalUSD, pctNW, nw, overlap, unpriced, heldUnpriced, largest }`, rows sorted
 * by combined value, largest first.
 */
export function tradeExposure() {
  const nw = netWorthUSD();

  // Portfolio side first: one bucket per NORMALISED ticker, so a second lot in a name he already
  // holds is the same name (rule of the holdings panel, `normTicker`, tests/quantity-registry
  // row `ticker.normalised`).
  const held = {};
  let heldUnpriced = 0;
  (S.holdings || []).forEach(h => {
    if (!h || !h.ticker) return;
    const vu = hVal(h).vU;
    if (!vu) { if (h.qty > 0) heldUnpriced++; return; }
    const k = normTicker(h.ticker);
    held[k] = (held[k] || 0) + vu;
  });

  const open = (S.trades || []).filter(t => t && t.status !== 'Closed' && t.type !== 'TimeDeposit');
  const rows = [];
  let unpriced = 0;
  open.forEach(t => {
    const p = S.prices[t.ticker] || S.prices[normTicker(t.ticker)];
    const qty = Number(t.qty) || 0;
    if (!p || !p.price || !qty) { unpriced++; return; }
    const lev = Number(t.leverage) || 1;
    // Magnitude, not direction. A short of 200 shares is 200 shares of exposure; the side is
    // shown beside it and does not shrink the position.
    const tradeUSD = Math.abs(toUSD(p.price * qty * lev, t.currency || 'USD'));
    if (!tradeUSD) return;
    const k = normTicker(t.ticker);
    const heldUSD = held[k] || 0;
    rows.push({
      ticker: t.ticker, key: k, side: t.side || '', lev,
      tradeUSD, heldUSD, combinedUSD: tradeUSD + heldUSD,
      tradePct: nw ? tradeUSD / nw * 100 : null,
      heldPct: nw ? heldUSD / nw * 100 : null,
      combinedPct: nw ? (tradeUSD + heldUSD) / nw * 100 : null,
    });
  });

  rows.sort((a, b) => b.combinedUSD - a.combinedUSD || String(a.ticker).localeCompare(String(b.ticker)));
  const totalUSD = rows.reduce((s, r) => s + r.tradeUSD, 0);
  return {
    rows, totalUSD, nw,
    pctNW: nw ? totalUSD / nw * 100 : null,
    overlap: rows.filter(r => r.heldUSD > 0).length,
    unpriced, heldUnpriced,
    largest: rows.length ? rows[0] : null,
  };
}
